import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./PhotoDetails.scss";

import arrowIcon from "./img/arrow-ui.png";
import closeIcon from "./img/close-ui.png";
import minusIcon from "./img/minus-ui.png";
import plusIcon from "./img/plus-ui.png";

export default function PhotoDetails({
  isOpen,
  open,
  photos = [],
  startIndex = 0,
  onClose,
}) {
  const opened = typeof isOpen === "boolean" ? isOpen : !!open;

  const [index, setIndex] = useState(
    Math.max(0, Math.min(startIndex || 0, (photos?.length || 1) - 1))
  );
  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const [closing, setClosing] = useState(false);
  const [isCoarse, setIsCoarse] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const [swipeDx, setSwipeDx] = useState(0);
  const [swipeAnimating, setSwipeAnimating] = useState(false);
  const swipeAnimToRef = useRef(null);

  const modalRef = useRef(null);
  const stageRef = useRef(null);
  const imgRef = useRef(null);

  const gestureRef = useRef({
    active: false,
    initialDistance: 0,
    initialScale: 1,
    lastX: 0,
    lastY: 0,
  });

  const lastTapRef = useRef({ t: 0, x: 0, y: 0 });
  const mouseRef = useRef({ active: false, lastX: 0, lastY: 0 });

  const swipeRef = useRef({
    active: false,
    intent: null,
    startX: 0,
    startY: 0,
    lastX: 0,
    lastY: 0,
    startedAt: 0,
  });

  const canNav = (photos?.length || 0) > 1;

  useEffect(() => {
    setIndex(Math.max(0, Math.min(startIndex || 0, (photos?.length || 1) - 1)));
  }, [startIndex, photos?.length]);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(pointer: coarse)");
    const update = () => setIsCoarse(!!mq.matches);
    update();
    mq.addEventListener?.("change", update);
    return () => mq.removeEventListener?.("change", update);
  }, []);

  const normalizeItem = useCallback((item) => {
    if (!item) return null;
    if (typeof item === "string") return { src: item, alt: "" };
    return { src: item.src || item.url, alt: item.alt || "" };
  }, []);

  const current = useMemo(
    () => normalizeItem(photos?.[index]),
    [photos, index, normalizeItem]
  );

  const prevItem = useMemo(() => {
    if (!canNav) return null;
    const i = (index - 1 + photos.length) % photos.length;
    return normalizeItem(photos?.[i]);
  }, [canNav, photos, index, normalizeItem]);

  const nextItem = useMemo(() => {
    if (!canNav) return null;
    const i = (index + 1) % photos.length;
    return normalizeItem(photos?.[i]);
  }, [canNav, photos, index, normalizeItem]);

  const clampScale = (s) => {
    const max = isCoarse ? 3 : 6;
    return Math.max(1, Math.min(max, s));
  };

  const resetView = useCallback(() => {
    setScale(1);
    setTranslate({ x: 0, y: 0 });
    setSwipeDx(0);
    setSwipeAnimating(false);
    swipeAnimToRef.current = null;
    swipeRef.current.active = false;
    swipeRef.current.intent = null;
  }, []);

  const close = useCallback(() => {
    if (closing) return;
    setClosing(true);
    setTimeout(() => {
      setClosing(false);
      resetView();
      onClose?.();
    }, 200);
  }, [closing, onClose, resetView]);

  const next = useCallback(() => {
    if (!canNav) return;
    setIndex((i) => (i + 1) % photos.length);
    resetView();
  }, [canNav, photos?.length, resetView]);

  const prev = useCallback(() => {
    if (!canNav) return;
    setIndex((i) => (i - 1 + photos.length) % photos.length);
    resetView();
  }, [canNav, photos?.length, resetView]);

  const getStagePointFromClient = (clientX, clientY) => {
    const stage = stageRef.current;
    if (!stage) return { x: 0, y: 0 };
    const rect = stage.getBoundingClientRect();
    return {
      x: clientX - rect.left - rect.width / 2,
      y: clientY - rect.top - rect.height / 2,
    };
  };

  const getBaseFitSize = () => {
    const stage = stageRef.current;
    const img = imgRef.current;

    if (!stage || !img || !img.naturalWidth || !img.naturalHeight) {
      const rect = stage?.getBoundingClientRect?.() || {
        width: window.innerWidth,
        height: window.innerHeight,
      };
      return { w: rect.width * 0.96, h: rect.height * 0.9 };
    }

    const rect = stage.getBoundingClientRect();
    const boxW = rect.width * 0.96;
    const boxH = rect.height * 0.9;
    const iw = img.naturalWidth;
    const ih = img.naturalHeight;
    const ar = iw / ih;
    const boxAr = boxW / boxH;

    if (ar >= boxAr) {
      const w = Math.min(boxW, iw);
      return { w, h: w / ar };
    } else {
      const h = Math.min(boxH, ih);
      return { w: h * ar, h };
    }
  };

  const clampTranslateToBounds = (tx, ty, s) => {
    const stage = stageRef.current;
    if (!stage) return { x: tx, y: ty };

    const rect = stage.getBoundingClientRect();
    const base = getBaseFitSize();
    const contentW = base.w * s;
    const contentH = base.h * s;

    const limitX = Math.max(0, (contentW - rect.width) / 2);
    const limitY = Math.max(0, (contentH - rect.height) / 2);

    return {
      x: Math.max(-limitX, Math.min(limitX, tx)),
      y: Math.max(-limitY, Math.min(limitY, ty)),
    };
  };

  const zoomAround = (deltaScale, px, py) => {
    setScale((sPrev) => {
      const sNext = clampScale(sPrev + deltaScale);
      if (sNext === sPrev) return sPrev;

      setTranslate((tPrev) => {
        const k = sNext / sPrev;
        const nx = px - k * (px - tPrev.x);
        const ny = py - k * (py - tPrev.y);
        return clampTranslateToBounds(nx, ny, sNext);
      });

      return sNext;
    });
  };

  const setZoomAt = (targetScale, px, py) => {
    setScale((sPrev) => {
      const sNext = clampScale(targetScale);
      const k = sNext / sPrev;

      setTranslate((tPrev) => {
        const nx = px - k * (px - tPrev.x);
        const ny = py - k * (py - tPrev.y);
        return clampTranslateToBounds(nx, ny, sNext);
      });

      return sNext;
    });
  };

  useEffect(() => {
    if (!opened) return;

    const html = document.documentElement;
    const body = document.body;

    html.classList.add("cv-no-page-zoom");
    body.classList.add("cv-no-page-zoom");

    const prevent = (e) => {
      try {
        if (e.cancelable) e.preventDefault();
      } catch {}
    };

    document.addEventListener("gesturestart", prevent, { passive: false });
    document.addEventListener("gesturechange", prevent, { passive: false });
    document.addEventListener("gestureend", prevent, { passive: false });

    const onWheelDoc = (e) => {
      if (!opened) return;
      if (e.ctrlKey) {
        const stage = stageRef.current;
        if (!stage) return;
        if (!stage.contains(e.target)) return;

        if (e.cancelable) e.preventDefault();
        const p = getStagePointFromClient(e.clientX, e.clientY);
        const delta = -e.deltaY * 0.002;
        zoomAround(delta, p.x, p.y);
      }
    };

    document.addEventListener("wheel", onWheelDoc, { passive: false });

    return () => {
      html.classList.remove("cv-no-page-zoom");
      body.classList.remove("cv-no-page-zoom");
      document.removeEventListener("gesturestart", prevent, { passive: false });
      document.removeEventListener("gesturechange", prevent, { passive: false });
      document.removeEventListener("gestureend", prevent, { passive: false });
      document.removeEventListener("wheel", onWheelDoc, { passive: false });
    };
  }, [opened]);

  useEffect(() => {
    if (!opened) return;

    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        close();
      }
      if (canNav && (e.key === "ArrowRight" || e.key === "d")) {
        e.preventDefault();
        next();
      }
      if (canNav && (e.key === "ArrowLeft" || e.key === "a")) {
        e.preventDefault();
        prev();
      }
    };

    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [opened, canNav, close, next, prev]);

  const distance = (t1, t2) => {
    const dx = t1.clientX - t2.clientX;
    const dy = t1.clientY - t2.clientY;
    return Math.hypot(dx, dy);
  };

  const midpoint = (t1, t2) => ({
    x: (t1.clientX + t2.clientX) / 2,
    y: (t1.clientY + t2.clientY) / 2,
  });

  const beginSwipeIfNeeded = (x, y) => {
    swipeRef.current.active = true;
    swipeRef.current.intent = null;
    swipeRef.current.startX = x;
    swipeRef.current.startY = y;
    swipeRef.current.lastX = x;
    swipeRef.current.lastY = y;
    swipeRef.current.startedAt = Date.now();
    setSwipeDx(0);
    setSwipeAnimating(false);
    swipeAnimToRef.current = null;
  };

  const onTouchStart = (e) => {
    if (!opened || swipeAnimating) return;

    if (isCoarse && e.touches.length === 1) {
      const now = Date.now();
      const x = e.touches[0].clientX;
      const y = e.touches[0].clientY;
      const dt = now - (lastTapRef.current.t || 0);
      const dx = x - (lastTapRef.current.x || 0);
      const dy = y - (lastTapRef.current.y || 0);
      lastTapRef.current = { t: now, x, y };

      if (dt < 300 && Math.hypot(dx, dy) < 20) {
        if (e.cancelable) e.preventDefault();
        const p = getStagePointFromClient(x, y);
        const target = scale > 1 ? 1 : 2.2;
        setZoomAt(target, p.x, p.y);
        return;
      }
    }

    if (e.touches.length === 1) {
      gestureRef.current.active = true;
      gestureRef.current.lastX = e.touches[0].clientX;
      gestureRef.current.lastY = e.touches[0].clientY;

      if (isCoarse && canNav && scale <= 1.02) {
        beginSwipeIfNeeded(e.touches[0].clientX, e.touches[0].clientY);
      } else {
        swipeRef.current.active = false;
        swipeRef.current.intent = null;
      }
    } else if (e.touches.length === 2) {
      swipeRef.current.active = false;
      swipeRef.current.intent = null;
      gestureRef.current.active = true;
      gestureRef.current.initialDistance = distance(e.touches[0], e.touches[1]);
      gestureRef.current.initialScale = scale;
    }
  };

  const onTouchMove = (e) => {
    if (!opened || !gestureRef.current.active) return;

    if (e.touches.length === 2) {
      const d = distance(e.touches[0], e.touches[1]);
      const mid = midpoint(e.touches[0], e.touches[1]);
      const p = getStagePointFromClient(mid.x, mid.y);

      setScale((sPrev) => {
        const base = gestureRef.current.initialScale || sPrev;
        const baseDist = gestureRef.current.initialDistance || d;
        const sNext = clampScale(base * (d / baseDist));
        if (sNext === sPrev) return sPrev;

        setTranslate((tPrev) => {
          const k = sNext / sPrev;
          const nx = p.x - k * (p.x - tPrev.x);
          const ny = p.y - k * (p.y - tPrev.y);
          return clampTranslateToBounds(nx, ny, sNext);
        });

        return sNext;
      });
      return;
    }

    if (e.touches.length !== 1) return;

    const x = e.touches[0].clientX;
    const y = e.touches[0].clientY;
    const dx = x - gestureRef.current.lastX;
    const dy = y - gestureRef.current.lastY;

    if (isCoarse && canNav && scale <= 1.02 && swipeRef.current.active) {
      const sx = swipeRef.current.startX;
      const sy = swipeRef.current.startY;
      const totalDx = x - sx;
      const totalDy = y - sy;
      const SLOP = 10;

      if (!swipeRef.current.intent) {
        if (Math.abs(totalDx) < SLOP && Math.abs(totalDy) < SLOP) {
          gestureRef.current.lastX = x;
          gestureRef.current.lastY = y;
          swipeRef.current.lastX = x;
          swipeRef.current.lastY = y;
          return;
        }
        swipeRef.current.intent =
          Math.abs(totalDx) > Math.abs(totalDy) * 1.15 ? "h" : "v";
      }

      if (swipeRef.current.intent === "h") {
        if (e.cancelable) e.preventDefault();

        const stage = stageRef.current;
        const w = stage?.getBoundingClientRect?.().width || window.innerWidth;
        const limit = w * 0.95;
        const clamped = Math.max(-limit, Math.min(limit, totalDx));
        setSwipeDx(clamped);

        gestureRef.current.lastX = x;
        gestureRef.current.lastY = y;
        swipeRef.current.lastX = x;
        swipeRef.current.lastY = y;
        return;
      }
    }

    if (scale <= 1.02) {
      setTranslate((t) => ({ x: 0, y: Math.max(0, t.y + dy) }));
      if (Math.abs(dy) > 22 && dy > 12) {
        close();
        return;
      }
    } else {
      setTranslate((t) => clampTranslateToBounds(t.x + dx, t.y + dy, scale));
    }

    gestureRef.current.lastX = x;
    gestureRef.current.lastY = y;
  };

  const finishSwipe = (direction) => {
    if (!canNav) return;
    const stage = stageRef.current;
    const w = stage?.getBoundingClientRect?.().width || window.innerWidth;

    swipeAnimToRef.current = direction;
    setSwipeAnimating(true);
    setSwipeDx(direction === "next" ? -w : w);
  };

  const cancelSwipe = () => {
    swipeAnimToRef.current = null;
    setSwipeAnimating(true);
    setSwipeDx(0);
  };

  const onTouchEnd = (e) => {
    if (e.touches && e.touches.length === 1) {
      gestureRef.current.active = true;
      gestureRef.current.lastX = e.touches[0].clientX;
      gestureRef.current.lastY = e.touches[0].clientY;

      if (isCoarse && canNav && scale <= 1.02 && !swipeAnimating) {
        beginSwipeIfNeeded(e.touches[0].clientX, e.touches[0].clientY);
      }
      return;
    }

    if (
      isCoarse &&
      canNav &&
      scale <= 1.02 &&
      swipeRef.current.active &&
      swipeRef.current.intent === "h"
    ) {
      const stage = stageRef.current;
      const w = stage?.getBoundingClientRect?.().width || window.innerWidth;

      const dt = Math.max(1, Date.now() - (swipeRef.current.startedAt || Date.now()));
      const vx = (swipeRef.current.lastX - swipeRef.current.startX) / dt;
      const THRESH = Math.max(60, w * 0.18);
      const FAST = 0.55;

      if (swipeDx > THRESH || vx > FAST) {
        finishSwipe("prev");
      } else if (swipeDx < -THRESH || vx < -FAST) {
        finishSwipe("next");
      } else {
        cancelSwipe();
      }

      gestureRef.current.active = false;
      swipeRef.current.active = false;
      swipeRef.current.intent = null;
      return;
    }

    gestureRef.current.active = false;
    swipeRef.current.active = false;
    swipeRef.current.intent = null;

    setScale((s) => (s < 1 ? 1 : s));
    if (scale <= 1.02) setTranslate({ x: 0, y: 0 });
  };

  const onTrackTransitionEnd = () => {
    if (!swipeAnimating) return;

    const dir = swipeAnimToRef.current;
    if (dir === "next") {
      setIndex((i) => (i + 1) % photos.length);
    } else if (dir === "prev") {
      setIndex((i) => (i - 1 + photos.length) % photos.length);
    }

    swipeAnimToRef.current = null;
    setSwipeAnimating(false);
    setSwipeDx(0);
    setTranslate({ x: 0, y: 0 });
  };

  useEffect(() => {
    if (!opened) return;
    const stage = stageRef.current;
    if (!stage) return;

    stage.addEventListener("touchstart", onTouchStart, { passive: false });
    stage.addEventListener("touchmove", onTouchMove, { passive: false });
    stage.addEventListener("touchend", onTouchEnd, { passive: false });
    stage.addEventListener("touchcancel", onTouchEnd, { passive: false });

    return () => {
      stage.removeEventListener("touchstart", onTouchStart, { passive: false });
      stage.removeEventListener("touchmove", onTouchMove, { passive: false });
      stage.removeEventListener("touchend", onTouchEnd, { passive: false });
      stage.removeEventListener("touchcancel", onTouchEnd, { passive: false });
    };
  }, [opened, scale, swipeDx, swipeAnimating, canNav]);

  useEffect(() => {
    if (!opened) return;
    const stage = stageRef.current;
    if (!stage) return;

    const onMouseDownLocal = (e) => {
      if (e.button !== 0 || scale <= 1) return;
      if (!stage.contains(e.target)) return;
      e.preventDefault();
      mouseRef.current.active = true;
      mouseRef.current.lastX = e.clientX;
      mouseRef.current.lastY = e.clientY;
      setIsDragging(true);
    };

    const onMouseMoveLocal = (e) => {
      if (!mouseRef.current.active) return;
      e.preventDefault();
      const dx = e.clientX - mouseRef.current.lastX;
      const dy = e.clientY - mouseRef.current.lastY;
      setTranslate((t) => clampTranslateToBounds(t.x + dx, t.y + dy, scale));
      mouseRef.current.lastX = e.clientX;
      mouseRef.current.lastY = e.clientY;
    };

    const onMouseUpLocal = () => {
      if (!mouseRef.current.active) return;
      mouseRef.current.active = false;
      setIsDragging(false);
    };

    stage.addEventListener("mousedown", onMouseDownLocal);
    window.addEventListener("mousemove", onMouseMoveLocal, { passive: false });
    window.addEventListener("mouseup", onMouseUpLocal);

    return () => {
      stage.removeEventListener("mousedown", onMouseDownLocal);
      window.removeEventListener("mousemove", onMouseMoveLocal, { passive: false });
      window.removeEventListener("mouseup", onMouseUpLocal);
    };
  }, [opened, scale]);

  const onWheelImage = (e) => {
    const p = getStagePointFromClient(e.clientX, e.clientY);
    const delta = -e.deltaY * 0.0015;
    zoomAround(delta, p.x, p.y);
  };

  const cursorStyle =
    !isCoarse && scale > 1 ? (isDragging ? "grabbing" : "grab") : "default";

  const shareOrOpen = useCallback(async () => {
    if (!current?.src) return;

    try {
      if (navigator.share) {
        await navigator.share({
          title: "Фото з Cvanto",
          url: current.src,
        });
        return;
      }
    } catch (err) {
      console.warn("Share failed, fallback to open:", err);
    }

    try {
      window.open(current.src, "_blank", "noopener,noreferrer");
    } catch (err) {
      console.warn("window.open failed:", err);
    }
  }, [current]);

  if (!opened || !current) return null;

  const trackTransform = `translate3d(calc(-100vw + ${swipeDx}px), 0, 0)`;

  return createPortal(
    <div
      ref={modalRef}
      className={`cv-photo-modal ${closing ? "is-closing" : "is-opening"}`}
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <button
        className="cvp-close"
        aria-label="Закрити"
        onClick={(e) => {
          e.stopPropagation();
          close();
        }}
      >
        <img src={closeIcon} alt="" aria-hidden="true" draggable={false} />
      </button>

      {canNav && (
        <>
          <button
            className="cvp-nav cvp-prev"
            aria-label="Попереднє"
            onClick={(e) => {
              e.stopPropagation();
              prev();
            }}
          >
            <img
              src={arrowIcon}
              alt=""
              className="cvp-nav-icon cvp-nav-icon--prev"
              aria-hidden="true"
              draggable={false}
            />
          </button>

          <button
            className="cvp-nav cvp-next"
            aria-label="Наступне"
            onClick={(e) => {
              e.stopPropagation();
              next();
            }}
          >
            <img
              src={arrowIcon}
              alt=""
              className="cvp-nav-icon cvp-nav-icon--next"
              aria-hidden="true"
              draggable={false}
            />
          </button>
        </>
      )}

      <div
        ref={stageRef}
        className="cvp-stage"
        style={{
          cursor: cursorStyle,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className={`cvp-track ${swipeAnimating ? "is-animating" : ""}`}
          style={{ transform: trackTransform }}
          onTransitionEnd={onTrackTransitionEnd}
        >
          <div className="cvp-slide">
            {prevItem?.src ? (
              <img
                className="cvp-img cvp-img--slide"
                src={prevItem.src}
                alt={prevItem.alt || ""}
                draggable={false}
              />
            ) : (
              <div />
            )}
          </div>

          <div
            className={`cvp-slide cvp-slide--current ${
              closing ? "wrap-out" : "wrap-in"
            }`}
          >
            <img
              ref={imgRef}
              className="cvp-img cvp-img--main"
              src={current.src}
              alt={current.alt || ""}
              draggable={false}
              onWheel={onWheelImage}
              style={{
                transform:
                  isCoarse && canNav && scale <= 1.02
                    ? `translate3d(0px, ${translate.y}px, 0) scale(${scale})`
                    : `translate3d(${translate.x}px, ${translate.y}px, 0) scale(${scale})`,
                transition:
                  isDragging || (isCoarse && scale > 2.4)
                    ? "none"
                    : "transform 80ms ease-out",
                willChange: "transform",
              }}
              onDoubleClick={(e) => {
                e.preventDefault();
                const p = getStagePointFromClient(e.clientX, e.clientY);
                const target = scale > 1 ? 1 : 2.2;
                setZoomAt(target, p.x, p.y);
              }}
            />
          </div>

          <div className="cvp-slide">
            {nextItem?.src ? (
              <img
                className="cvp-img cvp-img--slide"
                src={nextItem.src}
                alt={nextItem.alt || ""}
                draggable={false}
              />
            ) : (
              <div />
            )}
          </div>
        </div>
      </div>

      {!isCoarse && (
        <div className="cvp-zoombar" onClick={(e) => e.stopPropagation()}>
          <button
            className="cvp-zoom-btn"
            onClick={() => {
              const p = { x: 0, y: 0 };
              setScale((sPrev) => {
                const sNext = clampScale(sPrev - 0.2);
                const k = sNext / sPrev;
                setTranslate((tPrev) =>
                  clampTranslateToBounds(
                    p.x - k * (p.x - tPrev.x),
                    p.y - k * (p.y - tPrev.y),
                    sNext
                  )
                );
                return sNext;
              });
            }}
            aria-label="Зменшити"
          >
            <img src={minusIcon} alt="" aria-hidden="true" draggable={false} />
          </button>

          <input
            className="cvp-zoom-range"
            type="range"
            min={1}
            max={isCoarse ? 3 : 6}
            step={0.01}
            value={scale}
            onChange={(e) => {
              const nextVal = clampScale(parseFloat(e.target.value) || 1);
              const p = { x: 0, y: 0 };
              setZoomAt(nextVal, p.x, p.y);
            }}
          />

          <button
            className="cvp-zoom-btn"
            onClick={() => {
              const p = { x: 0, y: 0 };
              setScale((sPrev) => {
                const sNext = clampScale(sPrev + 0.2);
                const k = sNext / sPrev;
                setTranslate((tPrev) =>
                  clampTranslateToBounds(
                    p.x - k * (p.x - tPrev.x),
                    p.y - k * (p.y - tPrev.y),
                    sNext
                  )
                );
                return sNext;
              });
            }}
            aria-label="Збільшити"
          >
            <img src={plusIcon} alt="" aria-hidden="true" draggable={false} />
          </button>
        </div>
      )}
    </div>,
    document.body
  );
}